import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {useTaskApprove} from "../../../context/taskApprove.context";

export interface DragTask {
    id: number
    status: string
}

export type TaskDropPosition = 'before' | 'after' | 'end'
export type TaskDropMode = 'task' | 'column' | 'status' | 'list'

export interface TaskDropTarget {
    mode: TaskDropMode
    status: string
    position: TaskDropPosition
    taskId?: number
}

interface UseTaskDragAndDropOptions<T extends DragTask> {
    tasks: T[]
    onTasksCommitted?: (tasks: T[]) => void
    onTaskStatusPersist?: (task: T, status: string) => Promise<T | null | void>
    onOrderPersist?: (payload: {
        previousTasks: T[]
        nextTasks: T[]
        sourceTask: T | null
        droppedTask: T | null
    }) => Promise<void> | void
}

function getStatusEndIndex<T extends DragTask>(tasks: T[], status: string) {
    let lastMatchIndex = -1

    tasks.forEach((task, index) => {
        if (task.status === status) {
            lastMatchIndex = index
        }
    })

    return lastMatchIndex === -1 ? tasks.length : lastMatchIndex + 1
}

function isSameOrder<T extends DragTask>(left: T[], right: T[]) {
    if (left.length !== right.length) {
        return false
    }

    return left.every((task, index) => (
        task.id === right[index]?.id
        && task.status === right[index]?.status
    ))
}

export function moveTaskToTarget<T extends DragTask>(
    tasks: T[],
    draggedTaskId: number,
    target: TaskDropTarget
) {
    const draggedTask = tasks.find((task) => task.id === draggedTaskId)

    if (!draggedTask) {
        return tasks
    }

    if (target.taskId === draggedTaskId) {
        return tasks
    }

    const nextDraggedTask = target.mode === 'list' || draggedTask.status === target.status
        ? draggedTask
        : { ...draggedTask, status: target.status }

    const tasksWithoutDragged = tasks.filter((task) => task.id !== draggedTaskId)
    const targetIndex = target.taskId == null
        ? -1
        : tasksWithoutDragged.findIndex((task) => task.id === target.taskId)

    const insertIndex = targetIndex === -1
        ? target.mode === 'list'
            ? tasksWithoutDragged.length
            : getStatusEndIndex(tasksWithoutDragged, target.status)
        : target.position === 'after'
            ? targetIndex + 1
            : targetIndex

    const nextTasks = [...tasksWithoutDragged]
    nextTasks.splice(insertIndex, 0, nextDraggedTask as T)

    return isSameOrder(tasks, nextTasks) ? tasks : nextTasks
}

export function mergeOrderedTaskSubset<T extends DragTask>(tasks: T[], orderedSubset: T[]) {
    if (!orderedSubset.length) {
        return tasks
    }

    const orderedIds = new Set(orderedSubset.map((task) => task.id))
    const replacementQueue = [...orderedSubset]

    return tasks.map((task) => {
        if (!orderedIds.has(task.id)) {
            return task
        }

        return replacementQueue.shift() || task
    })
}

export default function useTaskDragAndDrop<T extends DragTask>({
    tasks,
    onTasksCommitted,
    onTaskStatusPersist,
    onOrderPersist,
}: UseTaskDragAndDropOptions<T>) {
    const {initiateCompleteTask} = useTaskApprove()
    const [orderedTasks, setOrderedTasks] = useState(tasks)
    const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null)
    const [dropTarget, setDropTarget] = useState<TaskDropTarget | null>(null)
    const [pendingTaskId, setPendingTaskId] = useState<number | null>(null)

    const originTasksRef = useRef<T[] | null>(null)
    const originDraggedTaskRef = useRef<T | null>(null)
    const didDropRef = useRef(false)

    useEffect(() => {
        if (draggedTaskId == null) {
            setOrderedTasks(tasks)
        }
    }, [draggedTaskId, tasks])

    const resetDragState = useCallback(() => {
        setDraggedTaskId(null)
        setDropTarget(null)
        originTasksRef.current = null
        originDraggedTaskRef.current = null
        didDropRef.current = false
    }, [])

    const previewMove = useCallback((target: TaskDropTarget) => {
        setDropTarget(target)
        setOrderedTasks((prev) => {
            if (draggedTaskId == null) {
                return prev
            }

            return moveTaskToTarget(prev, draggedTaskId, target)
        })
    }, [draggedTaskId])

    const beginDrag = useCallback((taskId: number) => {
        const currentTask = tasks.find((task) => task.id === taskId)

        if (!currentTask || pendingTaskId === taskId) {
            return
        }

        didDropRef.current = false
        originTasksRef.current = tasks
        originDraggedTaskRef.current = currentTask
        setDraggedTaskId(taskId)
        setDropTarget(null)
        setOrderedTasks(tasks)
    }, [pendingTaskId, tasks])

    const commitDrop = useCallback(async (target?: TaskDropTarget | null) => {
        //HERE DRAG N DROP
        if (draggedTaskId == null) {
            return
        }

        didDropRef.current = true

        const previousTasks = originTasksRef.current || tasks
        const effectiveTarget = target || dropTarget
        const nextTasks = effectiveTarget
            ? moveTaskToTarget(orderedTasks, draggedTaskId, effectiveTarget)
            : orderedTasks

        const sourceTask = originDraggedTaskRef.current || previousTasks.find((task) => task.id === draggedTaskId) || null
        const droppedTask = nextTasks.find((task) => task.id === draggedTaskId) || null

        setOrderedTasks(nextTasks)
        onTasksCommitted?.(nextTasks)
        setDraggedTaskId(null)
        setDropTarget(null)

        let shouldPersistOrder = true

        if (
            sourceTask
            && droppedTask
            && sourceTask.status !== droppedTask.status
            && onTaskStatusPersist
        ) {
            setPendingTaskId(sourceTask.id)

            try {
                if(droppedTask.status === 'complete'){
                    initiateCompleteTask(droppedTask, 'hereeeee 2');
                }else{
                    await onTaskStatusPersist(sourceTask, droppedTask.status)
                }
            } catch (error) {
                shouldPersistOrder = false
                setOrderedTasks(previousTasks)
                onTasksCommitted?.(previousTasks)
            } finally {
                setPendingTaskId(null)
            }
        }

        if (shouldPersistOrder && onOrderPersist) {
            try {
                await onOrderPersist({
                    previousTasks,
                    nextTasks,
                    sourceTask,
                    droppedTask,
                })
            } catch (error) {
                console.error('Failed to persist task order:', error)
            }
        }

        originTasksRef.current = null
        originDraggedTaskRef.current = null
        didDropRef.current = false
    }, [draggedTaskId, dropTarget, onOrderPersist, onTaskStatusPersist, onTasksCommitted, orderedTasks, tasks])

    const endDrag = useCallback(() => {
        if (didDropRef.current) {
            didDropRef.current = false
            return
        }

        if (originTasksRef.current) {
            setOrderedTasks(originTasksRef.current)
        }

        resetDragState()
    }, [resetDragState])

    const draggedTask = useMemo(() => (
        draggedTaskId == null
            ? null
            : orderedTasks.find((task) => task.id === draggedTaskId)
                || originDraggedTaskRef.current
    ), [draggedTaskId, orderedTasks])

    return {
        draggedTask,
        draggedTaskId,
        dropTarget,
        isDragging: draggedTaskId != null,
        orderedTasks,
        pendingTaskId,
        beginDrag,
        commitDrop,
        endDrag,
        previewMove,
    }
}
